import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, Video } from "remotion";

import { BrandLogo, BrandOutro, type OutroProps } from "./BrandOverlay";
import { CaptionOverlay, type CaptionedClipStyle, type RemotionCaptionEvent } from "./CaptionOverlay";

/**
 * Podgląd klipu w edytorze Studio (etap F1) — odtwarzany w `@remotion/player` w panelu,
 * bez renderu. Zamiast gotowego montażu z ffmpeg gra wprost z długiego nagrania:
 * każdy segment (po trimie ciszy, policzony tym samym `planClipCaptions` co serwer) to
 * osobna sekwencja z przewinięciem do właściwego miejsca źródła.
 *
 * Kadr (etap A): gdy klip ma plan kadru z ostatniego renderu, podgląd wycina z obrazu
 * dokładnie ten sam crop (te same klatki kluczowe X, ta sama zablokowana linia Y), bo
 * klatki kluczowe są w czasie i pikselach ŹRÓDŁA. Segmenty poza planem (przesunięty
 * początek/koniec) wracają do pełnego obrazu z pasami.
 */
export interface ClipPreviewSegment {
  /** Segment w ms ŹRÓDŁA. */
  fromMs: number;
  toMs: number;
}

export interface ClipPreviewTrack {
  y: number;
  xKeyframes: { tMs: number; x: number }[];
}

export interface ClipPreviewFraming {
  mode: "single" | "split" | "pad";
  sourceWidth: number;
  sourceHeight: number;
  crop: { w: number; h: number };
  segments: { fromMs: number; toMs: number; tracks: ClipPreviewTrack[] }[];
}

export interface ClipPreviewProps {
  /** Odtwarzalny URL nagrania źródłowego (mp4 z Bunny). */
  src: string;
  segments: ClipPreviewSegment[];
  events: RemotionCaptionEvent[];
  style: CaptionedClipStyle;
  framing?: ClipPreviewFraming | null;
  logoUrl?: string | null;
  outro?: OutroProps | null;
}

const msToFrames = (ms: number, fps: number) => Math.round((ms / 1000) * fps);

/** Ta sama krzywa co `cropXAt` w clip-framing.logic — powtórzona, bo kompozycja nie może importować logiki serwera. */
const xAt = (track: ClipPreviewTrack, tMs: number): number => {
  const keyframes = track.xKeyframes;
  if (!keyframes.length) return 0;
  if (tMs <= keyframes[0]!.tMs) return keyframes[0]!.x;
  for (let index = 1; index < keyframes.length; index += 1) {
    const previous = keyframes[index - 1]!;
    const next = keyframes[index]!;
    if (tMs <= next.tMs) {
      const span = next.tMs - previous.tMs;
      return span <= 0 ? next.x : previous.x + ((next.x - previous.x) * (tMs - previous.tMs)) / span;
    }
  }
  return keyframes[keyframes.length - 1]!.x;
};

const FramedVideo: React.FC<{
  src: string;
  segment: ClipPreviewSegment;
  framing: ClipPreviewFraming;
  track: ClipPreviewTrack;
  outputWidth: number;
  outputHeight: number;
}> = ({ src, segment, framing, track, outputWidth, outputHeight }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const sourceMs = segment.fromMs + (frame / fps) * 1000;
  const scale = outputWidth / framing.crop.w;
  const x = xAt(track, sourceMs);

  return (
    <div style={{ position: "relative", width: outputWidth, height: outputHeight, overflow: "hidden" }}>
      <Video
        src={src}
        startFrom={msToFrames(segment.fromMs, fps)}
        endAt={msToFrames(segment.toMs, fps)}
        pauseWhenBuffering
        style={{
          position: "absolute",
          left: -x * scale,
          top: -track.y * scale,
          width: framing.sourceWidth * scale,
          height: framing.sourceHeight * scale,
        }}
      />
    </div>
  );
};

const plannedSegmentFor = (framing: ClipPreviewFraming | null | undefined, segment: ClipPreviewSegment) => {
  if (!framing || framing.mode === "pad") return null;
  return (
    framing.segments.find((planned) => planned.fromMs <= segment.fromMs && planned.toMs >= segment.toMs) ??
    framing.segments.find((planned) => planned.fromMs < segment.toMs && planned.toMs > segment.fromMs) ??
    null
  );
};

export const ClipPreview: React.FC<ClipPreviewProps> = ({ src, segments, events, style, framing, logoUrl, outro }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const currentMs = (frame / fps) * 1000;

  // Frame positions come from cumulative milliseconds, so rounding does not drift against
  // the caption events (which planClipCaptions computes in exact milliseconds).
  let cursorMs = 0;
  const sequences = segments.map((segment, index) => {
    const from = msToFrames(cursorMs, fps);
    cursorMs += segment.toMs - segment.fromMs;
    const durationInFrames = Math.max(1, msToFrames(cursorMs, fps) - from);
    const planned = plannedSegmentFor(framing, segment);

    let content: React.ReactNode;
    if (framing && planned && framing.mode === "single" && planned.tracks[0]) {
      content = (
        <FramedVideo
          src={src}
          segment={segment}
          framing={framing}
          track={planned.tracks[0]}
          outputWidth={width}
          outputHeight={height}
        />
      );
    } else if (framing && planned && framing.mode === "split" && planned.tracks.length === 2) {
      content = (
        <div style={{ display: "flex", flexDirection: "column", width, height }}>
          {planned.tracks.map((track, side) => (
            <FramedVideo
              key={side}
              src={src}
              segment={segment}
              framing={framing}
              track={track}
              outputWidth={width}
              outputHeight={height / 2}
            />
          ))}
        </div>
      );
    } else {
      content = (
        <Video
          src={src}
          startFrom={msToFrames(segment.fromMs, fps)}
          endAt={msToFrames(segment.toMs, fps)}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          pauseWhenBuffering
        />
      );
    }

    // Parts of an ad sit minutes apart in a 10-minute file: premount the next segment so
    // its <video> has seeked and buffered before it becomes visible, instead of a black
    // frame under the captions while the browser fetches the range.
    return (
      <Sequence key={index} from={from} durationInFrames={durationInFrames} premountFor={fps * 4}>
        {content}
      </Sequence>
    );
  });

  const spokenMs = segments.reduce((sum, segment) => sum + (segment.toMs - segment.fromMs), 0);

  return (
    <AbsoluteFill style={{ backgroundColor: "black" }}>
      {sequences}
      <CaptionOverlay currentMs={currentMs} events={events} style={style} />
      <BrandLogo logoUrl={logoUrl} />
      <BrandOutro outro={outro} fromMs={spokenMs} />
    </AbsoluteFill>
  );
};
